///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { UserAuthorities } from '../../services/user/user-authorities';
import { ProjectGtthBomSummaryPopupComponent } from './bom_summary/bom-summary.component';
import { GtthImportNodesPopupComponent } from './import-nodes/import-nodes-modal.component';
import { GtthImportTopologyPopupComponent } from './import-topology/import-topology-modal.component';
import { ProjectGtthBomReportPopupComponent } from './bom_report/bom-report.component';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, Routes } from '@angular/router';
import { ProjectGtthAdvancedPlanConfigPopupComponent } from './advanced_plan/advanced-config.component';
import { GtthAutoconnectPopupComponent } from './auto-connect/autoconnect-modal.component';
import { GtthReportConnectedSitesPopupComponent } from './reports/connected-sites/report-connected-sites.component';
import { GtthReportConstraintCompliancePopupComponent } from './reports/constraint-compliance/report-constraint-compliance.component';
import { GtthReportP2PInfoPopupComponent } from './reports/p2p-info/report-p2p-info.component';
import { GtthReportP2MPInfoPopupComponent } from './reports/p2mp-info/report-p2mp-info.component';

import {
    GtthReportConstraintComplianceMaxHopsNodesListPopupComponent,
    GtthReportConstraintComplianceMaxLinksNodesListPopupComponent
} from './reports/constraint-compliance-nodes-list/report-constraint-compliance-nodes-list.component';
import { GtthRequestDesignQuotationPopupComponent } from './request-design-quotation/request-design-quotation-modal.component';
import { GtthReportNodesWithInterferencePopupComponent } from './reports/nodes-with-interference/nodes-with-interference.component';
import { GtthDisabledEntitiesPopupComponent } from './disabled-entities/disabled-entities.component';
import { GtthConfigRingsErrorReportPopupComponent } from './reports/config-rings-error-report/config-rings-error-report.component';
import { GtthHouseDetectionPopupComponent } from './house-detection/house-detection-modal.component';
import { GtthReportOverloadedSectorsPopupComponent } from './reports/overloaded-sectors-report/report-overloaded-sectors.component';
import { GtthManualDesignPopupComponent } from './manual-design/manual-design.component';
import { ProjectGtthTunnelsReportPopupComponent } from './reports/tunnels/tunnels-report.component';
import { GtthAnIpInfoPopupComponent } from './reports/an-ip-info/an-ip-info.component';
import { GtthProjectStateService } from '../logical-topology/engine/project-state.service';
import { inject } from '@angular/core';
import { canActivateUser } from '../../shared/auth/user-route-access-service';

const pageTitle = 'project.home.title';

export const canActivateProjectDialog: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const projectStateService = inject(GtthProjectStateService);
    const canActivate = projectStateService.projectIsOpened() && projectStateService.getProjectId() !== undefined;
    if (canActivate) {
        return true;
    }

    const router = inject(Router);
    const navigationSegments: any[] = state.root.children[0].url.map((segment) => segment.path);
    navigationSegments.push({ outlets: { popup: null } });
    return router.createUrlTree(navigationSegments);
};

export const projectModalWindowsRoute: Routes = [
    {
        path: 'project-view/bom-report',
        component: ProjectGtthBomReportPopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_ALL,
            pageTitle
        },
        canActivate: [canActivateUser, canActivateProjectDialog],
        outlet: 'popup'
    },
    {
        path: 'project-view/bom-summary',
        component: ProjectGtthBomSummaryPopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_ALL,
            pageTitle
        },
        canActivate: [canActivateUser, canActivateProjectDialog],
        outlet: 'popup'
    },
    {
        path: 'project-view/tunnels-report',
        component: ProjectGtthTunnelsReportPopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_ALL,
            pageTitle
        },
        canActivate: [canActivateUser, canActivateProjectDialog],
        outlet: 'popup'
    },
    {
        path: 'project-view/an-ip-report',
        component: GtthAnIpInfoPopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_ALL,
            pageTitle
        },
        canActivate: [canActivateUser, canActivateProjectDialog],
        outlet: 'popup'
    },
    {
        path: 'project-view/advanced-plan-config',
        component: ProjectGtthAdvancedPlanConfigPopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_EDIT,
            pageTitle
        },
        canActivate: [canActivateUser, canActivateProjectDialog],
        outlet: 'popup'
    },
    {
        path: 'project-view/manual-design',
        component: GtthManualDesignPopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_EDIT,
            pageTitle
        },
        canActivate: [canActivateUser, canActivateProjectDialog],
        outlet: 'popup'
    },
    {
        path: 'project-view/import-nodes',
        component: GtthImportNodesPopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_EDIT,
            pageTitle
        },
        canActivate: [canActivateUser, canActivateProjectDialog],
        outlet: 'popup'
    },
    {
        path: 'project-view/import-topology',
        component: GtthImportTopologyPopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_EDIT,
            pageTitle
        },
        canActivate: [canActivateUser, canActivateProjectDialog],
        outlet: 'popup'
    },
    {
        path: 'project-view/auto-connect',
        component: GtthAutoconnectPopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_EDIT,
            pageTitle
        },
        canActivate: [canActivateUser, canActivateProjectDialog],
        outlet: 'popup'
    },
    {
        path: 'project-view/constraint-compliance',
        component: GtthReportConstraintCompliancePopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_ALL,
            pageTitle
        },
        canActivate: [canActivateUser, canActivateProjectDialog],
        outlet: 'popup'
    },
    {
        path: 'project-view/constraint-compliance-node-list/max-hops',
        component: GtthReportConstraintComplianceMaxHopsNodesListPopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_ALL,
            pageTitle
        },
        canActivate: [canActivateUser, canActivateProjectDialog],
        outlet: 'popup'
    },
    {
        path: 'project-view/constraint-compliance-node-list/max-links',
        component: GtthReportConstraintComplianceMaxLinksNodesListPopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_ALL,
            pageTitle
        },
        canActivate: [canActivateUser, canActivateProjectDialog],
        outlet: 'popup'
    },
    {
        path: 'project-view/nodes-with-interference',
        component: GtthReportNodesWithInterferencePopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_ALL,
            pageTitle
        },
        canActivate: [canActivateUser, canActivateProjectDialog],
        outlet: 'popup'
    },
    {
        path: 'project-view/connected-sites',
        component: GtthReportConnectedSitesPopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_ALL,
            pageTitle
        },
        canActivate: [canActivateUser, canActivateProjectDialog],
        outlet: 'popup'
    },
    {
        path: 'project-view/p2p-info',
        component: GtthReportP2PInfoPopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_ALL,
            pageTitle
        },
        canActivate: [canActivateUser, canActivateProjectDialog],
        outlet: 'popup'
    },
    {
        path: 'project-view/p2mp-info',
        component: GtthReportP2MPInfoPopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_ALL,
            pageTitle
        },
        canActivate: [canActivateUser, canActivateProjectDialog],
        outlet: 'popup'
    },
    {
        path: 'project-view/request-design-quotation',
        component: GtthRequestDesignQuotationPopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_ALL,
            pageTitle
        },
        canActivate: [canActivateUser, canActivateProjectDialog],
        outlet: 'popup'
    },
    {
        path: 'project-view/disabled-entities',
        component: GtthDisabledEntitiesPopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_ALL,
            pageTitle
        },
        canActivate: [canActivateUser, canActivateProjectDialog],
        outlet: 'popup'
    },
    {
        path: 'project-view/nodes-design-error/:title',
        component: GtthConfigRingsErrorReportPopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_ALL,
            pageTitle
        },
        canActivate: [canActivateUser, canActivateProjectDialog],
        outlet: 'popup'
    },
    {
        path: 'project-view/house-detection',
        component: GtthHouseDetectionPopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_EDIT,
            pageTitle
        },
        canActivate: [canActivateUser, canActivateProjectDialog],
        outlet: 'popup'
    },
    {
        path: 'project-view/overloaded-sectors',
        component: GtthReportOverloadedSectorsPopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_ALL,
            pageTitle
        },
        canActivate: [canActivateUser, canActivateProjectDialog],
        outlet: 'popup'
    }
];
